/** "Design with AI" from a Sketch (pure, tested in carry.test.ts): what the Designer says about the drawn panel
 * layout on the automatic option (the layout document's `carried`, the editor document's `layout_carry`), as catalog
 * keys and their values (resolve with t()). */
import type { S } from "../api/client";
import type { MessageKey } from "../i18n/keys";

export interface CarryText {
  key: MessageKey;
  values?: Record<string, number>;
}

export interface CarryMessage {
  tone: "ok" | "info";
  title: CarryText;
  detail: CarryText;
  /** A second sentence after the detail: drawn panels that did not fit and were left out. */
  missing: CarryText | null;
}

/** The message for the automatic option's first layout, or null when it was not made from a Sketch. */
export function carryMessage(carry: S["LayoutCarry"] | null | undefined): CarryMessage | null {
  if (!carry) return null;
  const dropped = carry.panels_drawn - carry.panels_carried - (carry.status === "partial" ? carry.panels_fresh ?? 0 : 0);
  const missing: CarryText | null = dropped > 0 && carry.status !== "fresh" ? { key: "editor.carry.missing", values: { count: dropped } } : null;
  if (carry.status === "carried")
    return {
      tone: "ok",
      title: { key: "editor.carry.carriedTitle", values: { count: carry.panels_carried } },
      detail: { key: "editor.carry.carriedDetail" },
      missing,
    };
  if (carry.status === "partial") {
    const matched = carry.faces.filter((f) => f.face_id && (f.panels_carried ?? 0) > 0).length;
    const fresh = carry.panels_fresh ?? 0;
    return {
      tone: "info",
      title: { key: "editor.carry.partialTitle", values: { carried: carry.panels_carried, fresh } },
      detail: { key: "editor.carry.partialDetail", values: { matched, faces: carry.faces.length } },
      missing,
    };
  }
  return {
    tone: "info",
    title: { key: "editor.carry.freshTitle" },
    detail: { key: "editor.carry.freshDetail" },
    missing: null,
  };
}
